/**
 * The special-offers popup: *which* offer, and *when*.
 *
 * ── What counts as an offer ──────────────────────────────────────────────────
 * Exactly what the shop's Featured shelf puts in front of a player, in the same
 * order of importance and nothing else — so the popup can never advertise a deal
 * the shop does not show:
 *
 *   1. live limited offers (discounted bundles and packs, soonest to end first),
 *   2. the highlighted ("Popular") pack, when it is not already on sale,
 *   3. the weekly experience and credit boosts.
 *
 * The Research Lab boosts are not featured on the shelf and so are not here.
 * Packs are bought with money, so a pack offer only ever *leads to* the shop; a
 * bundle or a boost is bought with diamonds and can be bought from the popup,
 * through a second "are you sure" step the popup itself owns.
 *
 * ── When ─────────────────────────────────────────────────────────────────────
 * Once shortly after signing in, then again after a random 35–90 minutes. The
 * deadline is module state — not component state — so it survives the garage
 * being left for a battle and entered again: the popup never fires *in* the
 * arena, it simply comes due and is shown when the garage is next on screen.
 * Signing out forgets it, so the next sign-in is a fresh "upon auth"; a reload
 * does not, because the deadline is also kept per player in `localStorage`.
 *
 * Pure functions take the clock as an argument, in the manner of the shop's own
 * offer helpers, so none of this needs fake timers to test.
 */
import { ref, watch } from 'vue';

import type { BoostOffer, DiamondPack, PremiumShop, TankBundle } from '@/net/api';
import { useSession } from '@/state/session';

export type OfferSection = 'featured' | 'diamonds' | 'bundles' | 'boosts';

export interface SpecialOffer {
  /** Stable across polls, so the rotation can remember what it showed last. */
  key: string;
  kind: 'pack' | 'bundle' | 'boost';
  /** The id the purchase endpoints take (`pack_depot`, `bundle_ashfall`, `boost_xp_7d`). */
  id: string;
  /** For a bundle sale, the offer id that pins the quote on the purchase. */
  offerId?: string;
  title: string;
  /** One line under the title. */
  line: string;
  /** Diamonds, for the two kinds that are bought with them. */
  diamondCost?: number;
  /** The standing price while a sale runs, for the struck-through figure. */
  fullDiamondCost?: number;
  /** Money, for a pack (minor units of `currency`). */
  priceCents?: number;
  fullPriceCents?: number;
  currency?: string;
  /** Whole percent off, when a sale runs. */
  discountPct?: number;
  /** ISO instant the sale closes. */
  endsAt?: string;
  /** Where "View in shop" lands. */
  section: OfferSection;
  /** Whether the popup may offer to buy it outright (diamond-priced only). */
  buyable: boolean;
  /** A bundle is drawn from its own hull and turret, the others from store art. */
  hullId?: string;
  turretId?: string;
  /** `/assets/store/<id>.webp` for packs and boosts. */
  image?: string;
}

const storeArt = (id: string): string => `/assets/store/${id.replace(/_/g, '-')}.webp`;

const live = (offer: { endsAt: string } | null | undefined, nowMs: number): boolean =>
  !!offer && Date.parse(offer.endsAt) > nowMs;

const pct = (current: number, full: number): number =>
  full > 0 ? Math.round((1 - current / full) * 100) : 0;

/** A boost's effect in the words the shop card uses. */
export function boostLine(boost: BoostOffer): string {
  const span = boost.hours >= 168 && boost.hours % 168 === 0 ? '1 week' : `${boost.hours}h`;
  if (boost.kind === 'XP') return `EXP ×${boost.multiplier} for ${span}`;
  if (boost.kind === 'LOOT') return `Credits ×${boost.multiplier} for ${span}`;
  return `Research time ÷${boost.multiplier} for ${span}`;
}

function packOffer(pack: DiamondPack, nowMs: number): SpecialOffer {
  const sale = live(pack.offer, nowMs) ? pack.offer : null;
  return {
    key: `pack:${pack.id}`,
    kind: 'pack',
    id: pack.id,
    title: pack.name,
    line: `${pack.total.toLocaleString('en-GB')} diamonds`,
    priceCents: pack.priceCents,
    fullPriceCents: sale?.fullPriceCents,
    currency: pack.currency,
    discountPct: sale ? pct(pack.priceCents, sale.fullPriceCents) : undefined,
    endsAt: sale?.endsAt,
    section: 'diamonds',
    buyable: false,
    image: storeArt(pack.id),
  };
}

function bundleOffer(bundle: TankBundle, nowMs: number): SpecialOffer {
  const sale = live(bundle.offer, nowMs) ? bundle.offer : null;
  return {
    key: `bundle:${bundle.id}`,
    kind: 'bundle',
    id: bundle.id,
    offerId: sale?.id,
    title: bundle.name,
    line: `${bundle.hullName} with ${bundle.turretName}`,
    diamondCost: bundle.diamondCost,
    fullDiamondCost: sale?.fullDiamondCost,
    discountPct: sale ? pct(bundle.diamondCost, sale.fullDiamondCost) : undefined,
    endsAt: sale?.endsAt,
    section: 'bundles',
    buyable: bundle.available,
    hullId: bundle.hullId,
    turretId: bundle.turretId,
  };
}

function boostOffer(boost: BoostOffer): SpecialOffer {
  return {
    key: `boost:${boost.id}`,
    kind: 'boost',
    id: boost.id,
    title: boost.name,
    line: boostLine(boost),
    diamondCost: boost.diamondCost,
    section: 'boosts',
    buyable: true,
    image: storeArt(boost.id),
  };
}

/** The featured offers, most important first. Empty when nothing is featured. */
export function featuredOffers(
  shop: PremiumShop | null | undefined,
  bundles: readonly TankBundle[],
  nowMs: number,
): SpecialOffer[] {
  if (!shop) return [];
  const byEnd = (a: SpecialOffer, b: SpecialOffer): number =>
    Date.parse(a.endsAt ?? '') - Date.parse(b.endsAt ?? '');

  const sales: SpecialOffer[] = [
    ...bundles.filter((b) => live(b.offer, nowMs) && b.available).map((b) => bundleOffer(b, nowMs)),
    ...shop.packs.filter((p) => live(p.offer, nowMs)).map((p) => packOffer(p, nowMs)),
  ].sort(byEnd);

  const popular = shop.packs
    .filter((p) => p.highlight && !live(p.offer, nowMs))
    .map((p) => packOffer(p, nowMs));

  const weekly = shop.boosts
    .filter((b) => b.kind !== 'RESEARCH' && b.hours >= 168)
    .map(boostOffer);

  return [...sales, ...popular, ...weekly];
}

/** The offer after `lastKey` in the list, wrapping; the first when it is unknown. */
export function nextOffer(offers: readonly SpecialOffer[], lastKey: string | null): SpecialOffer | null {
  if (offers.length === 0) return null;
  const at = lastKey ? offers.findIndex((o) => o.key === lastKey) : -1;
  return offers[(at + 1) % offers.length];
}

// ─── Schedule ────────────────────────────────────────────────────────────────

/** Between these two the popup nags again. */
export const NAG_MIN_MS = 35 * 60_000;
export const NAG_MAX_MS = 90 * 60_000;
/** After signing in, a beat for the garage to settle before the first one. */
export const FIRST_OFFER_DELAY_MS = 2_500;

/** A random gap in `[NAG_MIN_MS, NAG_MAX_MS]`. `random` is injectable for tests. */
export function nagGap(random: () => number = Math.random): number {
  return Math.round(NAG_MIN_MS + random() * (NAG_MAX_MS - NAG_MIN_MS));
}

/**
 * The schedule is kept per player in `localStorage` as well as in memory, so a
 * reload is not a sign-in: without it every refresh re-armed the 2.5 s "upon
 * auth" popup and restarted the rotation. It is a pacing hint and nothing more —
 * hand-editing it can only move this player's own popup, and every value read
 * back is clamped into `[now + FIRST_OFFER_DELAY_MS, now + NAG_MAX_MS]`.
 */
const STORAGE_PREFIX = 'sow.offers.';
const MAX_KEY_LENGTH = 128;

interface StoredSchedule {
  dueAt: number;
  lastKey: string | null;
}

/** When the next popup is due, in epoch ms; `null` = not armed (signed out). */
const dueAt = ref<number | null>(null);
let lastKey: string | null = null;
/** Whose schedule is loaded — the storage key, and the guard against a stale one. */
let owner: string | null = null;

function readStored(playerId: string): StoredSchedule | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + playerId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { dueAt: at, lastKey: key } = parsed as Record<string, unknown>;
    if (typeof at !== 'number' || !Number.isFinite(at)) return null;
    const validKey = typeof key === 'string' && key.length <= MAX_KEY_LENGTH ? key : null;
    return { dueAt: at, lastKey: validKey };
  } catch {
    // Storage denied or JSON that is not: behave like a fresh sign-in.
    return null;
  }
}

function writeStored(): void {
  if (!owner || dueAt.value === null) return;
  try {
    const value: StoredSchedule = { dueAt: dueAt.value, lastKey };
    window.localStorage.setItem(STORAGE_PREFIX + owner, JSON.stringify(value));
  } catch {
    // A schedule that failed to save costs one extra popup after a reload, nothing more.
  }
}

function forgetStored(playerId: string): void {
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + playerId);
  } catch {
    // Nothing to forget if storage is refused.
  }
}

const clampDue = (at: number, nowMs: number): number =>
  Math.min(Math.max(at, nowMs + FIRST_OFFER_DELAY_MS), nowMs + NAG_MAX_MS);

/**
 * Adopts another tab's later deadline and rotation, so two open garages do not
 * each show the popup on their own clock. Only ever pushes the deadline back.
 */
function syncFromStorage(nowMs: number): void {
  if (!owner || dueAt.value === null) return;
  const stored = readStored(owner);
  if (!stored || stored.dueAt <= dueAt.value) return;
  dueAt.value = clampDue(stored.dueAt, nowMs);
  lastKey = stored.lastKey;
}

/**
 * Arms the schedule when the garage mounts. Idempotent for the same player: a
 * return from battle, or a second mount, never moves the deadline. A reload picks
 * the stored deadline back up (never sooner than the settle beat); only a player
 * with nothing stored — a real first sign-in — gets the 2.5 s "upon auth" popup.
 * `playerId` `null` keeps the schedule in memory only.
 */
export function armSpecialOffers(playerId: string | null, nowMs: number = Date.now()): void {
  if (dueAt.value !== null && owner === playerId) return;
  owner = playerId;
  const stored = playerId ? readStored(playerId) : null;
  dueAt.value = stored ? clampDue(stored.dueAt, nowMs) : nowMs + FIRST_OFFER_DELAY_MS;
  lastKey = stored?.lastKey ?? null;
  writeStored();
}

/** Whether a popup is due right now. */
export function offerDue(nowMs: number = Date.now()): boolean {
  syncFromStorage(nowMs);
  return dueAt.value !== null && nowMs >= dueAt.value;
}

/**
 * Takes the offer that is due and schedules the next nag 35–90 minutes out —
 * whether or not the player buys or dismisses, because the gap is a pacing rule,
 * not a reward for an answer. `null` (and a re-armed next-due) when nothing is
 * featured, so an empty shop does not make the check run hot.
 */
export function takeDueOffer(
  offers: readonly SpecialOffer[],
  nowMs: number = Date.now(),
  random: () => number = Math.random,
): SpecialOffer | null {
  syncFromStorage(nowMs);
  const offer = nextOffer(offers, lastKey);
  dueAt.value = nowMs + nagGap(random);
  if (offer) lastKey = offer.key;
  writeStored();
  return offer;
}

/** Forgets the in-memory schedule — tests, and the sign-out below. */
export function resetSpecialOffers(): void {
  dueAt.value = null;
  lastKey = null;
  owner = null;
}

/** Test seam: the current deadline. */
export function specialOfferDueAt(): number | null {
  return dueAt.value;
}

/**
 * Signing out drops the schedule, stored copy included, so the next sign-in is a
 * fresh "upon auth". A reload never passes through here: `player` starts `null`
 * and only ever goes to a player, which is not a change this callback acts on.
 */
const { player } = useSession();
watch(
  () => player.value?.id ?? null,
  (id, previous) => {
    if (id !== null) return;
    if (previous) forgetStored(previous);
    resetSpecialOffers();
  },
);
